import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button, Spinner } from 'modules/v2/common/AtomicDesign/atoms';
import { getModAudiencePreview, getModAudienceTags } from 'modules/api/digitalServices/mailOnDemand';
import { getRouteMarketingHub } from 'modules/v2/routes/navigation';
import { cn } from 'modules/v2/common/utils/cn';

import strings, { formatMessage } from '../../strings';
import { AUDIENCE_MODE, AUDIENCE_PREVIEW_DEBOUNCE_MS } from '../../constants';
import { formatCredits, parseTagsInput } from '../../utils';

const PREVIEW_ERROR = {
  NO_MARKETING_HUB: 'no-marketing-hub',
  RETRIABLE: 'retriable',
};

const TAGS_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  LOADED: 'loaded',
  // The tags endpoint failed (422/502/etc.) — we don't dead-end the member;
  // we fall back to the manual free-text input so they can still target tags.
  ERROR: 'error',
};

/** Up to two initials from a contact name, for the sample-list avatar chip. */
const getInitials = (name) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const letters = parts.length === 1 ? parts[0].slice(0, 2) : parts[0][0] + parts[parts.length - 1][0];
  return letters.toUpperCase();
};

const AudienceStep = ({ mode, tagsInput, onModeChange, onTagsChange, onBack, onContinue, submitting }) => {
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);

  const [availableTags, setAvailableTags] = useState([]);
  const [tagsStatus, setTagsStatus] = useState(TAGS_STATUS.IDLE);

  const tags = parseTagsInput(tagsInput);
  const tagsKey = tags.join(',');
  const selectedTags = new Set(tags);

  // Load the member's GHL tags whenever we enter (or switch into) tags mode.
  // On any failure (no Marketing Hub / GHL read error) we fall back to the
  // manual free-text input rather than blocking the member.
  useEffect(() => {
    if (mode !== AUDIENCE_MODE.TAGS) return undefined;

    let cancelled = false;
    setTagsStatus(TAGS_STATUS.LOADING);

    getModAudienceTags()
      .then((data) => {
        if (cancelled) return;
        setAvailableTags(Array.isArray(data?.tags) ? data.tags : []);
        setTagsStatus(TAGS_STATUS.LOADED);
      })
      .catch(() => {
        if (cancelled) return;
        setAvailableTags([]);
        setTagsStatus(TAGS_STATUS.ERROR);
      });

    return () => {
      cancelled = true;
    };
  }, [mode]);

  // Toggle a tag in/out of the selection while preserving the existing
  // contract: `onTagsChange` always receives the same comma-joined string the
  // free-text input produced, so the debounced preview/submit flow is unchanged.
  const toggleTag = (tag) => {
    const next = new Set(tags);

    if (next.has(tag)) {
      next.delete(tag);
    } else {
      next.add(tag);
    }

    onTagsChange([...next].join(', '));
  };

  useEffect(() => {
    let cancelled = false;

    if (mode === AUDIENCE_MODE.TAGS && tags.length === 0) {
      setPreview(null);
      setPreviewError(null);
      setPreviewLoading(false);
      return undefined;
    }

    setPreviewLoading(true);
    setPreviewError(null);

    const timer = setTimeout(() => {
      getModAudiencePreview(mode, tags)
        .then((data) => {
          if (cancelled) return;
          setPreview(data);
          setPreviewLoading(false);
        })
        .catch((error) => {
          if (cancelled) return;
          setPreview(null);
          setPreviewLoading(false);

          if (error?.response?.status === 422) {
            setPreviewError(PREVIEW_ERROR.NO_MARKETING_HUB);
          } else {
            setPreviewError(PREVIEW_ERROR.RETRIABLE);
          }
        });
    }, AUDIENCE_PREVIEW_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, tagsKey, retryNonce]);

  const canContinue =
    !submitting &&
    !previewLoading &&
    previewError !== PREVIEW_ERROR.NO_MARKETING_HUB &&
    (mode === AUDIENCE_MODE.ALL || tags.length > 0);

  const renderManualTagsInput = () => (
    <div>
      <label htmlFor="mod-tags-input" className="mb-1 block text-sm font-medium">
        {strings.audience.tagsLabel}
      </label>
      <input
        id="mod-tags-input"
        data-testid="mod-tags-input"
        type="text"
        value={tagsInput}
        onChange={(event) => onTagsChange(event.target.value)}
        placeholder={strings.audience.tagsPlaceholder}
        className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-800 focus:outline-none"
      />
    </div>
  );

  const renderTagsSelector = () => {
    if (tagsStatus === TAGS_STATUS.LOADING || tagsStatus === TAGS_STATUS.IDLE) {
      return (
        <div className="flex items-center gap-2 text-neutral-500" data-testid="mod-tags-loading">
          <Spinner />
          <span>{strings.audience.tagsLoading}</span>
        </div>
      );
    }

    // Endpoint failed (422 no-Marketing-Hub / 502 / network) — fall back to the
    // free-text input so the member is never dead-ended.
    if (tagsStatus === TAGS_STATUS.ERROR) {
      return (
        <div data-testid="mod-tags-error">
          <p className="mb-2 text-sm text-neutral-600">{strings.audience.tagsLoadError}</p>
          {renderManualTagsInput()}
        </div>
      );
    }

    // The selection round-trips through a comma-joined string, so a tag value
    // containing a literal comma can't be toggled (it would split into two
    // pseudo-tags). Drop those values so they never render a dead chip.
    const selectableTags = availableTags.filter((tag) => !tag.includes(','));

    // Loaded but the member has no selectable tags yet — keep a manual fallback
    // so they can still target by tag if they know one.
    if (selectableTags.length === 0) {
      return (
        <div data-testid="mod-tags-empty">
          <h4 className="mb-1 font-semibold">{strings.audience.tagsEmptyTitle}</h4>
          <p className="mb-3 text-sm text-neutral-600">{strings.audience.tagsEmptyBody}</p>
          {renderManualTagsInput()}
        </div>
      );
    }

    return (
      <div data-testid="mod-tags-selector">
        <span className="mb-1 block text-sm font-medium">{strings.audience.tagsSelectorLabel}</span>
        <p className="mb-3 text-sm text-neutral-500">{strings.audience.tagsSelectorHint}</p>
        <div className="flex flex-wrap gap-2" role="group" aria-label={strings.audience.tagsSelectorLabel}>
          {selectableTags.map((tag) => {
            const checked = selectedTags.has(tag);
            return (
              <button
                type="button"
                key={tag}
                aria-pressed={checked}
                data-testid={`mod-tag-option-${tag}`}
                onClick={() => toggleTag(tag)}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-sm transition-colors',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500',
                  checked
                    ? 'border-primary-500 bg-primary-500 text-white'
                    : 'border-neutral-200 bg-white text-neutral-700 hover:border-primary-300',
                )}
              >
                {tag}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderPreview = () => {
    if (previewError === PREVIEW_ERROR.NO_MARKETING_HUB) {
      return (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="mod-no-marketing-hub">
          <h4 className="mb-1 font-semibold">{strings.audience.noMarketingHubTitle}</h4>
          <p className="mb-3 text-sm text-neutral-600">{strings.audience.noMarketingHubBody}</p>
          <Link to={getRouteMarketingHub()} className="text-sm font-semibold underline">
            {strings.audience.noMarketingHubCta}
          </Link>
        </div>
      );
    }

    if (previewError === PREVIEW_ERROR.RETRIABLE) {
      return (
        <div className="flex items-center gap-3 rounded-lg border border-error-500 bg-error-50 p-4">
          <span className="text-sm">{strings.audience.previewRetriableError}</span>
          <Button color="outlined" size="xs" onClick={() => setRetryNonce((nonce) => nonce + 1)}>
            {strings.common.retry}
          </Button>
        </div>
      );
    }

    if (previewLoading) {
      return (
        <div className="flex items-center gap-2 text-neutral-500">
          <Spinner />
          <span>{strings.audience.previewLoading}</span>
        </div>
      );
    }

    if (!preview) return null;

    const total = preview.totalCount ?? 0;
    const mailable = preview.estimatedMailable ?? 0;
    const mailablePercent =
      total > 0
        ? Math.max(0, Math.min(100, Math.round((mailable / total) * 100)))
        : 0;

    return (
      <div
        className="rounded-xl border border-neutral-200 bg-white p-5"
        data-testid="mod-audience-preview"
      >
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-neutral-700">
            {strings.audience.previewSummaryTitle}
          </h3>
          <span className="rounded-full bg-primary-50 px-2 py-0.5 text-xs font-semibold text-primary-700">
            {strings.audience.previewEstimateLabel}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-neutral-100 bg-neutral-50 p-3">
            {/* Full sentence for screen readers; the visible split (number +
                short label) is hidden from the a11y tree to avoid a double read. */}
            <span className="sr-only">
              {formatMessage(strings.audience.previewTotal, {
                totalCount: total,
              })}
            </span>
            <div aria-hidden="true">
              <div
                className="text-2xl font-bold text-neutral-800"
                data-testid="mod-audience-total"
              >
                {formatCredits(total)}
              </div>
              <div className="text-xs text-neutral-500">
                {strings.audience.previewTotalLabel}
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-primary-100 bg-primary-50/50 p-3">
            <span className="sr-only">
              {formatMessage(strings.audience.previewMailable, {
                estimatedMailable: mailable,
              })}
            </span>
            <div aria-hidden="true">
              <div
                className="text-2xl font-bold text-primary-700"
                data-testid="mod-audience-mailable"
              >
                {formatCredits(mailable)}
              </div>
              <div className="text-xs text-neutral-500">
                {strings.audience.previewMailableLabel}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-3">
          <div
            className="h-2 w-full overflow-hidden rounded-full bg-neutral-100"
            role="progressbar"
            aria-valuenow={mailablePercent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={formatMessage(strings.audience.previewMailableShare, {
              percent: mailablePercent,
            })}
          >
            <div
              className="h-full rounded-full bg-primary-500"
              style={{ width: `${mailablePercent}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-neutral-400">
            {strings.audience.previewEstimateNote}
          </p>
        </div>

        {preview.sample?.length > 0 && (
          <div className="mt-4 border-t border-neutral-100 pt-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-400">
              {strings.audience.previewSampleTitle}
            </div>
            <ul className="flex flex-col gap-2">
              {preview.sample.map((contact, index) => {
                const location = [contact.city, contact.state]
                  .filter((part) => part && part.trim() !== '')
                  .join(', ');
                return (
                  <li
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    className="flex items-center gap-2.5"
                  >
                    <span
                      aria-hidden="true"
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700"
                    >
                      {getInitials(contact.name)}
                    </span>
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-sm font-medium text-neutral-800">
                        {contact.name}
                      </span>
                      {location ? (
                        <span className="truncate text-xs text-neutral-500">
                          {location}
                        </span>
                      ) : (
                        <span className="text-xs text-neutral-400">
                          {strings.audience.previewSampleNoAddress}
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <h2 className="mb-1 text-lg font-semibold">{strings.audience.title}</h2>
      <p className="mb-6 text-neutral-500">{strings.audience.subtitle}</p>

      <div className="mb-4 flex gap-3">
        {[
          { value: AUDIENCE_MODE.ALL, label: strings.audience.modeAll },
          { value: AUDIENCE_MODE.TAGS, label: strings.audience.modeTags },
        ].map((option) => (
          <button
            type="button"
            key={option.value}
            data-testid={`mod-audience-mode-${option.value}`}
            onClick={() => onModeChange(option.value)}
            className={cn(
              'rounded-lg border px-4 py-2 text-sm',
              mode === option.value
                ? 'border-neutral-800 bg-neutral-800 text-white'
                : 'border-neutral-200 bg-white text-neutral-800 hover:border-neutral-400',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      {mode === AUDIENCE_MODE.TAGS && (
        <div className="mb-4 max-w-xl">{renderTagsSelector()}</div>
      )}

      <div className="mb-8 min-h-[80px]">{renderPreview()}</div>

      <div className="flex justify-between">
        <Button color="outlined" onClick={onBack} disabled={submitting}>
          {strings.common.back}
        </Button>
        <Button onClick={onContinue} disabled={!canContinue} data-testid="mod-audience-continue">
          {submitting ? strings.audience.submitting : strings.audience.continue}
        </Button>
      </div>
    </div>
  );
};

export default AudienceStep;
